import React, { useState, useCallback } from 'react';
import {
  Modal,
  Text,
  BlockStack,
  TextField,
} from '@shopify/polaris';

const OrderHelpModal = ({ 
  isOpen, 
  onClose, 
  orderId,
  generationId,
  kidName,
  onSubmit,
  isSubmitting = false
}) => {
  const [helpNotes, setHelpNotes] = useState('');
  const [helpNotesError, setHelpNotesError] = useState('');

  const handleSubmit = useCallback(async () => {
    // Validate help notes
    if (!helpNotes.trim()) {
      setHelpNotesError('Help notes are required');
      return;
    }

    if (helpNotes.trim().length < 10) {
      setHelpNotesError('Help notes must be at least 10 characters long');
      return;
    }

    setHelpNotesError('');

    try {
      await onSubmit(helpNotes.trim());
      // Reset form and close modal on success
      setHelpNotes('');
      onClose();
    } catch (error) {
      // Error handling is done in parent component
      console.error('Error submitting help notes:', error);
    }
  }, [helpNotes, onSubmit, onClose]);

  const handleClose = useCallback(() => {
    setHelpNotes('');
    setHelpNotesError('');
    onClose();
  }, [onClose]);

  const handleHelpNotesChange = useCallback((value) => {
    setHelpNotes(value);
    if (helpNotesError) {
      setHelpNotesError('');
    }
  }, [helpNotesError]);

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={<div style={{padding: '16px'}}>{`Request Order Help`}</div>}
      primaryAction={{
        content: 'Submit Help Request',
        onAction: handleSubmit,
        loading: isSubmitting,
        disabled: isSubmitting || !helpNotes.trim(),
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: handleClose,
          disabled: isSubmitting,
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Text variant="bodyMd" tone="subdued">
            Submit a help request for this order. This will add a "❌ Order Help" tag to the Shopify order and include your notes as a comment.
          </Text>
          
          {orderId && (
            <BlockStack gap="200">
              <Text variant="bodyMd" fontWeight="semibold">Order Details:</Text>
              <Text variant="bodyMd" tone="subdued">Order ID: {orderId}</Text>
              {generationId && <Text variant="bodyMd" tone="subdued">Generation ID: {generationId}</Text>}
              {kidName && <Text variant="bodyMd" tone="subdued">Kid Name: {kidName}</Text>}
            </BlockStack>
          )}

          <TextField
            label="Help Notes"
            value={helpNotes}
            onChange={handleHelpNotesChange}
            multiline={4}
            placeholder="Please describe the issue or help needed for this order..."
            error={helpNotesError}
            helpText="Minimum 10 characters required. Be specific about the issue or help needed."
            autoComplete="off"
          />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default OrderHelpModal;